import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-policy-overlay',
  standalone: true,
  imports: [CommonModule],
  template: `
    <dialog #dialogEl class="policy-overlay-dialog" (cancel)="onCancel($event)">
      <div class="policy-overlay-header">
        <h3>{{ title }}</h3>
        <button class="close-btn" (click)="close()" aria-label="Close">&times;</button>
      </div>
      <div class="policy-overlay-body" #bodyEl (scroll)="onScroll()">
        <ng-container *ngTemplateOutlet="contentTpl"></ng-container>
      </div>
      <div class="policy-overlay-footer">
        <button class="agree-btn" [disabled]="!agreeEnabled" (click)="agree()">I Agree</button>
      </div>
    </dialog>
  `,
  styles: [`
    :host {
      display: contents;
    }

    /* Hidden by default — a native <dialog> is only shown when [open] is set
       (added by showModal()). This prevents the flex rule below from forcing
       the closed dialog visible. */
    .policy-overlay-dialog:not([open]) {
      display: none;
    }

    .policy-overlay-dialog[open] {
      display: flex;
    }

    .policy-overlay-dialog {
      border: none;
      border-radius: 8px;
      padding: 0;
      max-width: 720px;
      width: 90vw;
      max-height: 80vh;
      flex-direction: column;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
      overflow: hidden;
    }

    .policy-overlay-dialog::backdrop {
      background: rgba(0, 0, 0, 0.5);
    }

    .policy-overlay-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .policy-overlay-header h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      padding: 4px 8px;
      color: #666;
      border-radius: 4px;
    }

    .close-btn:hover {
      background: #f0f0f0;
      color: #333;
    }

    .policy-overlay-body {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      min-height: 0;
    }

    .policy-overlay-footer {
      display: flex;
      justify-content: flex-end;
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .agree-btn {
      padding: 8px 24px;
      border: none;
      border-radius: 4px;
      font-size: 0.95rem;
      font-weight: 500;
      cursor: pointer;
      background-color: #007bff;
      color: #fff;
      transition: opacity 0.2s;
    }

    .agree-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .agree-btn:not(:disabled):hover {
      background-color: #0056b3;
    }
  `],
})
export class PolicyOverlayComponent implements OnDestroy {
  @Input() title = '';
  @Input() contentTpl: TemplateRef<unknown> | null = null;
  /** Unique identifier for the document so session persistence is keyed per doc. */
  @Input() documentId = '';

  @Output() scrolledToEnd = new EventEmitter<void>();
  @Output() agreed = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dialogEl') dialogEl!: ElementRef<HTMLDialogElement>;
  @ViewChild('bodyEl') bodyEl!: ElementRef<HTMLDivElement>;

  agreeEnabled = false;
  hasScrolledToEnd = false;

  /** Scroll threshold (px) — content within this distance of the bottom counts as "read". */
  private static readonly SCROLL_THRESHOLD = 24;

  /**
   * Per-session set of document IDs whose agree action has been enabled.
   * Static so it persists across component open/close cycles within the same session.
   */
  private static sessionEnabledDocs = new Set<string>();

  /** Active ResizeObserver for detecting content-size changes. */
  private resizeObserver: ResizeObserver | null = null;

  /** Opens the overlay using the native dialog top-layer mechanism. */
  openModal(): void {
    const dialog = this.dialogEl?.nativeElement;
    if (!dialog) return;

    // Session persistence: if this document was already enabled, restore immediately.
    const docKey = this.getDocKey();
    if (PolicyOverlayComponent.sessionEnabledDocs.has(docKey)) {
      this.enableAgree();
    }

    dialog.showModal();
    this.scheduleFitCheck(0);
    this.observeBodyResize();
  }

  /** Closes the dialog and emits the closed event. */
  close(): void {
    const dialog = this.dialogEl?.nativeElement;
    if (!dialog) return;

    this.disconnectResizeObserver();
    dialog.close();
    this.closed.emit();
  }

  /** Emits the agreed event and closes the overlay. */
  agree(): void {
    if (!this.agreeEnabled) return;
    this.agreed.emit();
    this.close();
  }

  /** Handles scroll events on the body container. */
  onScroll(): void {
    if (this.agreeEnabled) return;
    this.checkScrollPosition();
  }

  /** Prevents the dialog from being closed via Escape without emitting the closed event. */
  onCancel(event: Event): void {
    event.preventDefault();
    this.close();
  }

  ngOnDestroy(): void {
    this.disconnectResizeObserver();
  }

  /**
   * Checks if the scroll position is within the threshold of the bottom.
   * Uses a 24px threshold per spec.
   */
  private checkScrollPosition(): void {
    const el = this.bodyEl?.nativeElement;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= PolicyOverlayComponent.SCROLL_THRESHOLD) {
      this.enableAgree();
    }
  }

  /**
   * Schedules a fit check to enable the agree button immediately when content
   * fits within the visible scroll region without a scrollbar.
   */
  private scheduleFitCheck(attempt: number): void {
    requestAnimationFrame(() => {
      if (this.agreeEnabled) return;

      const el = this.bodyEl?.nativeElement;
      if (el && el.clientHeight > 0) {
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom <= PolicyOverlayComponent.SCROLL_THRESHOLD) {
          this.enableAgree();
        }
        return;
      }

      // Layout not ready yet — retry on the next frame (bounded retries).
      if (attempt < 30) {
        this.scheduleFitCheck(attempt + 1);
      }
    });
  }

  /**
   * Sets up a ResizeObserver on the body scroll container so that when content
   * size changes (e.g. lazy-loaded content), we re-check whether content fits.
   */
  private observeBodyResize(): void {
    this.disconnectResizeObserver();

    const el = this.bodyEl?.nativeElement;
    if (!el) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.agreeEnabled) return;
      this.checkScrollPosition();
    });
    this.resizeObserver.observe(el);
  }

  /** Disconnects and cleans up the ResizeObserver. */
  private disconnectResizeObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /** Marks the agree button as enabled (monotonic — never goes back to disabled). */
  private enableAgree(): void {
    if (this.agreeEnabled) return;
    this.agreeEnabled = true;
    this.hasScrolledToEnd = true;
    this.scrolledToEnd.emit();

    // Persist in the session so close/reopen keeps agree enabled.
    PolicyOverlayComponent.sessionEnabledDocs.add(this.getDocKey());
  }

  /** Returns the document key used for session persistence. Falls back to title. */
  private getDocKey(): string {
    return this.documentId || this.title;
  }

  /**
   * Resets the static session state. Exposed for testing purposes only.
   * @internal
   */
  static _resetSessionState(): void {
    PolicyOverlayComponent.sessionEnabledDocs.clear();
  }
}
